/**
 * Display helpers for the Catalogue migration pages (v1 completion F4):
 * labels for the backend's steps and review statuses, and how a customer's
 * step reads in the list. Pure and headless; the step itself and every count
 * are the backend's (views_module computes them in SQL).
 */

import type {
  CatalogueMigrationOwnerRow,
  CatalogueMigrationStatus,
  CatalogueMigrationStep,
} from "../data/api-types";
import { formatCount, formatDateOrDash } from "./formatters";

export const MIGRATION_STEP_LABELS: Record<CatalogueMigrationStep, string> = {
  not_started: "Not started",
  in_review: "In review",
  ready: "Ready to cut over",
  cut_over: "Cut over",
  done: "Done",
};

/** The list's filter chips, in the order the page shows them (`done` is shown only when a customer is there). */
export const MIGRATION_STEP_FILTERS: CatalogueMigrationStep[] = [
  "not_started",
  "in_review",
  "ready",
  "cut_over",
  "done",
];

export const REVIEW_STATUS_LABELS: Record<CatalogueMigrationStatus, string> = {
  becomes_item: "Becomes an item",
  needs_decision: "Needs a decision",
  unlinked: "Unlinked",
  history_only: "History only",
  left_for_martin: "Left for Martin",
  stays_on_tharstern: "Stays on Tharstern",
  brandhub: "BrandHub's mapping",
};

/** The review list's filter chips, in the order the page shows them. */
export const REVIEW_STATUS_FILTERS: CatalogueMigrationStatus[] = [
  "becomes_item",
  "needs_decision",
  "unlinked",
  "history_only",
  "left_for_martin",
  "stays_on_tharstern",
  "brandhub",
];

/** One thing a customer's step has or has not got through: "Reviewed", "Items", "Artwork 12/14". */
export interface MigrationStepChip {
  label: string;
  isDone: boolean;
}

export interface MigrationStepReading {
  /** What the step says in words when it has no checklist ("Not started", "In review: 2 decisions", "Cut over 2 Oct 2026"). */
  text: string | null;
  /** The checklist of a customer whose review is done and whose items and artwork are on their way. */
  chips: MigrationStepChip[];
}

/** How the list's Step cell reads for one customer. */
export function readMigrationStep(
  owner: Pick<
    CatalogueMigrationOwnerRow,
    | "step"
    | "open_question_count"
    | "reviewed_by"
    | "item_count"
    | "items_applied"
    | "artwork_expected"
    | "artwork_supplied"
    | "cut_over_at"
  >,
): MigrationStepReading {
  switch (owner.step) {
    case "not_started":
      return { text: MIGRATION_STEP_LABELS.not_started, chips: [] };
    case "cut_over":
      return {
        text: `Cut over ${formatDateOrDash(owner.cut_over_at)}`,
        chips: [],
      };
    case "done":
      return { text: MIGRATION_STEP_LABELS.done, chips: [] };
    case "in_review":
    case "ready": {
      const n = owner.open_question_count;
      const decisions = `${formatCount(n)} ${n === 1 ? "decision" : "decisions"}`;
      if (owner.reviewed_by === null) {
        return {
          text:
            n > 0
              ? `${MIGRATION_STEP_LABELS.in_review}: ${decisions}`
              : MIGRATION_STEP_LABELS.in_review,
          chips: [],
        };
      }
      return {
        text:
          n > 0
            ? `${decisions} open`
            : owner.step === "ready"
              ? MIGRATION_STEP_LABELS.ready
              : null,
        chips: [
          { label: "Reviewed", isDone: true },
          {
            label: "Items",
            isDone: owner.items_applied >= owner.item_count,
          },
          {
            label:
              owner.artwork_supplied >= owner.artwork_expected
                ? "Artwork"
                : `Artwork ${formatCount(owner.artwork_supplied)}/${formatCount(owner.artwork_expected)}`,
            isDone: owner.artwork_supplied >= owner.artwork_expected,
          },
        ],
      };
    }
  }
}
